import { Injectable } from '@angular/core';
import {
  AuthConfig,
  OAuthEvent,
  OAuthInfoEvent,
  OAuthService,
  TokenResponse,
} from 'angular-oauth2-oidc';
import jwt_decode from 'jwt-decode';
import { filter } from 'rxjs/operators';
import { UserDto } from 'src/portal/user-api/models/user-dto';
import { UsersService } from 'src/portal/user-api/services/users.service';
import { environment } from '../../../environments/environment';
import { UserProfileDataService } from './user-profile.service';

function GetAppRootUrl() {
  return window.location.origin;
}

function GetCurrentUrl(returnUrl?: string) {
  return window.location.origin + (returnUrl || window.location.pathname);
}

const OauthConfig: AuthConfig = {
  issuer: environment.issuerUrl,
  logoutUrl: `${environment.issuerUrl}logout?returnTo=${environment.logoutUrl}&client_id=${environment.clientId}`,
  postLogoutRedirectUri: encodeURIComponent(GetAppRootUrl()),
  redirectUri: GetCurrentUrl(),
  clientId: environment.clientId,
  responseType: 'code',
  showDebugInformation: environment.production,
  scope: environment.scope,
  customQueryParams: {
    audience: environment.audience,
  },
};

class UserInfoModel {
  preferred_username: string;
  email: string;
  name: string;
  sub: string;
  picture: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  public userAsInfo: UserDto;
  public isUserAs: boolean = false;

  constructor(
    private oauthService: OAuthService,
    private usersService: UsersService,
    private userProfileDataService: UserProfileDataService
  ) {}

  private activeRefreshRequest: Promise<TokenResponse>;
  private discoveryDocumentRequest: Promise<any>;

  private isTokenExpiresEvent(event: OAuthEvent): event is OAuthInfoEvent {
    return event.type == 'token_expires';
  }

  async configure() {
    this.oauthService.configure(OauthConfig);
    await this.loadDiscoveryDocument();

    this.oauthService.events
      .pipe(
        filter((i) => this.isTokenExpiresEvent(i) && i.info == 'access_token')
      )
      .subscribe(() => {
        this.refreshToken();
      });
  }

  public async refreshToken() {
    if (!this.activeRefreshRequest) {
      this.activeRefreshRequest = this.oauthService.refreshToken();
    }

    const tokenResponse = await this.activeRefreshRequest;
    delete this.activeRefreshRequest;

    return tokenResponse;
  }

  private loadDiscoveryDocument() {
    this.discoveryDocumentRequest =
      this.discoveryDocumentRequest ??
      this.oauthService?.loadDiscoveryDocument().catch(() => {
        delete this.discoveryDocumentRequest;
      });

    return this.discoveryDocumentRequest;
  }

  async validateToken(returnUrl?: string) {
    await this.oauthService.tryLoginCodeFlow();

    if (this.oauthService.hasValidAccessToken()) {
      await this.userProfileDataService.getUserData();
      return true;
    } else if (this.oauthService.getRefreshToken()) {
      try {
        await this.refreshToken();
        return true;
      } catch (error) {
        this.openLoginPage(returnUrl);
        return false;
      }
    } else {
      this.openLoginPage(returnUrl);
      return false;
    }
  }

  openLoginPage(returnUrl?: string) {
    localStorage.setItem('redirectUrl', returnUrl);
    this.oauthService.redirectUri = GetCurrentUrl("/");
    this.oauthService.initCodeFlow();
  }

  logout() {
    this.oauthService.revokeTokenAndLogout();
  }

  getUserClaims() {
    return this.oauthService.getIdentityClaims() as UserInfoModel;
  }

  getAccessToken() {
    return this.oauthService.getAccessToken();
  }

  checkAdminRole(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }
    try {
      const decoded: any = jwt_decode(token);
      const roles: Array<string> = decoded['permissions'] || [];
      return roles.includes('buyercaddy:admin');
    } catch (error) {
      return false;
    }
  }

  async checkAuthAs() {
    const token = this.getAccessToken();
    if (!token) {
      console.warn('Токен отсутствует в checkAuthAs');
      this.isUserAs = false;
      return;
    }
    try {
      const decoded: any = jwt_decode(token);
      if (decoded['https://salescaddy.ai/on-behalf-user-id']) {
        this.isUserAs = true;
        this.getCurrentUserInfo();
      } else {
        this.isUserAs = false;
      }
    } catch (error) {
      console.error('Ошибка декодирования токена в checkAuthAs:', error);
      this.isUserAs = false;
    }
  }

  async authAs(): Promise<any> {
    try {
      await this.oauthService.refreshToken().then(() => {
        this.isUserAs = true;
        this.getCurrentUserInfo();
      });
    } catch (error) {
      console.error('Ошибка в authAs:', error);
      throw error;
    }
  }

  async getCurrentUserInfo() {
    this.usersService.loadUser().subscribe({
      next: (res) => {
        this.userAsInfo = res;
      },
      error: (err) => {
        console.error('Ошибка загрузки данных пользователя:', err);
      }
    });
  }
}